// src/stores/bankrollStore.ts
import { create } from 'zustand';
import { BankrollState, BankrollTx, TxKind, uid } from '../domain/types';
import { indexedDBRepo } from '../services/trackerRepo';
import { applyTx, initialBankrollState, createStakeTx, createPayoutTx } from '../services/bankrollEngine';

interface BankrollStoreState {
  state: BankrollState;
  transactions: BankrollTx[];
  isLoading: boolean;

  load: () => Promise<void>;
  deposit: (amount: number, note?: string) => Promise<void>;
  withdraw: (amount: number, note?: string) => Promise<void>;
  adjust: (amount: number, note?: string) => Promise<void>;
  reset: (initial?: number) => Promise<void>;
  setInitial: (initial: number) => Promise<void>;

  // getters
  available: number; // current - openStake
  totalProfit: number; // current - initial
  roi: number;
}

export const useBankrollStore = create<BankrollStoreState>((set, get) => ({
  state: initialBankrollState(),
  transactions: [],
  isLoading: false,

  load: async () => {
    set({ isLoading: true });
    const [bank, txs] = await Promise.all([
      indexedDBRepo.getBankroll(),
      // buscar txs do IndexedDB
      (async () => {
        const db = await (await import('idb')).openDB('rush-props', 1);
        return db.getAll('txs');
      })(),
    ]);
    set({ state: bank, transactions: txs.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime()), isLoading: false });
  },

  deposit: async (amount, note) => {
    if (amount <= 0) return;
    const tx = {
      id: uid(),
      ts: new Date().toISOString(),
      kind: TxKind.DEPOSIT,
      refType: 'pick' as const,
      refId: uid(),
      amount,
      balanceAfter: get().state.current + amount,
      note: note ?? 'Depósito',
    };
    const newState = applyTx(get().state, tx);
    await indexedDBRepo.setBankroll(newState);
    await indexedDBRepo.addTx(tx);
    set({ state: newState, transactions: [tx, ...get().transactions] });
  },

  withdraw: async (amount, note) => {
    if (amount <= 0) return;
    const available = get().state.current - get().state.openStake;
    if (amount > available) throw new Error('Saldo disponível insuficiente');
    const tx = {
      id: uid(),
      ts: new Date().toISOString(),
      kind: TxKind.WITHDRAW,
      refType: 'pick' as const,
      refId: uid(),
      amount: -amount,
      balanceAfter: get().state.current - amount,
      note: note ?? 'Saque',
    };
    const newState = applyTx(get().state, tx);
    await indexedDBRepo.setBankroll(newState);
    await indexedDBRepo.addTx(tx);
    set({ state: newState, transactions: [tx, ...get().transactions] });
  },

  adjust: async (amount, note) => {
    const tx = {
      id: uid(),
      ts: new Date().toISOString(),
      kind: TxKind.ADJUSTMENT,
      refType: 'pick' as const,
      refId: uid(),
      amount,
      balanceAfter: get().state.current + amount,
      note: note ?? 'Ajuste manual',
    };
    const newState = applyTx(get().state, tx);
    await indexedDBRepo.setBankroll(newState);
    await indexedDBRepo.addTx(tx);
    set({ state: newState, transactions: [tx, ...get().transactions] });
  },

  reset: async (initial = 100) => {
    const newState = initialBankrollState(initial);
    await indexedDBRepo.setBankroll(newState);
    // limpa txs
    const db = await (await import('idb')).openDB('rush-props', 1);
    await db.clear('txs');
    set({ state: newState, transactions: [] });
  },

  setInitial: async (initial) => {
    if (!Number.isFinite(initial) || initial < 0) return;
    const previous = get().state;
    const state = { ...previous, initial, current: Math.max(0, previous.current + initial - previous.initial) };
    await indexedDBRepo.setBankroll(state);
    set({ state });
  },

  get available() {
    return get().state.current - get().state.openStake;
  },
  get totalProfit() {
    return get().state.current - get().state.initial;
  },
  get roi() {
    const { initial, current } = get().state;
    return initial > 0 ? Math.round(((current - initial) / initial) * 10000) / 100 : 0;
  },
}));
